import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import useEmojiAnimations from '@src/hooks/useEmojiAnimations';
import useTabVisibility from '@src/hooks/useTabVisibility';
import useVideoDimensions from './hooks/useVideoDimensions';
import useVideoContextMenu from './hooks/useVideoContextMenu';
import useConsumerControl from './hooks/useConsumerControl';

import SocketService from '@src/services/SocketService';
import useRoomContext from '@src/contexts/useRoomContext';
import { useSettingsContext } from '@src/contexts/SettingsContext';

import { debounce } from '@src/utils';
import { createElementColor, getMediaStreamSource } from '@src/helpers';

import {
  useRoomStore,
  usePeersStore,
  useDrawerStore,
  useRoomConfigStore,
  useRoomActionsStore,
  useVideoStreamStore,
  useWebinarRoomStore,
  useVirtualBackgroundStore,
} from '@src/stores';

import { EWebinarSockets } from '@src/constants/webinar.constants';

import { EMeetingType, IVideoStream, TEmojiId, TVideoObjectFit } from '@src/types';

const useVideoBox = (videoStream: IVideoStream, isPipBox?: boolean, objectFit?: TVideoObjectFit) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isPeerTalking, setIsPeerTalking] = useState(false);
  const [hoveredVideoBox, setHoveredVideoBox] = useState(false);
  const [isMenuPopperOpened, setIsMenuPopperOpened] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  // Store cleanup function in ref to avoid dependency issues
  const cleanupRef = useRef<(() => void) | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Stable empty stream reference to prevent creating new streams on every render
  const emptyStreamRef = useRef<MediaStream | null>(null);

  const {
    peerId,
    stream: videoMediaStream,
    audioVolume = 0,
    isPinned,
    isCurrentUser,
    type,
    muted: isVideoAudioMuted,
  } = videoStream;

  const { selectedVideoObjectFit } = useSettingsContext();

  const { isMobileDevice, isHost, isHostOrCohost, isCurrentPeerSharing } = useRoomContext();

  const isTabActive = useTabVisibility();
  const { animations } = useEmojiAnimations();

  // Add consumer control logic
  const { handleVisibilityChange } = useConsumerControl(videoStream.id);
  const videoDimensions = useVideoDimensions(videoRef, containerRef, isPipBox);

  const { isFloatingButtonEnabled } = useRoomConfigStore(
    useShallow((state) => ({
      isFloatingButtonEnabled: state.magicPenConfig?.floatingButton,
    }))
  );

  const { originalStream, modifiedStream } = useVideoStreamStore(
    useShallow((state) => ({
      originalStream: state.originalStream,
      modifiedStream: state.modifiedStream,
    }))
  );

  const { meetingType } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
    }))
  );

  const { peers, raisedHandsQueue, raiseHand, lowerHand } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
      raiseHand: state.raiseHand,
      lowerHand: state.lowerHand,
      raisedHandsQueue: state.raisedHandsQueue,
    }))
  );

  const { viewersPinnedPeerUids, toggleViewerPinForPeerUid } = useWebinarRoomStore(
    useShallow((state) => ({
      viewersPinnedPeerUids: state.viewersPinnedPeerUids,
      toggleViewerPinForPeerUid: state.toggleViewerPinForPeerUid,
    }))
  );

  const { isDrawerOpen } = useDrawerStore(useShallow((state) => ({ isDrawerOpen: state.isDrawerOpen })));

  // Get peer color from store based on backend hue_color
  const peerColor = usePeersStore(useShallow((state) => state.getPeerColor(peerId)));

  const { pip } = useVideoStreamStore(
    useShallow((state) => ({
      pip: state.pip,
    }))
  );

  const handleMouseEnter = useCallback(() => {
    setHoveredVideoBox(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredVideoBox(false);
    setIsMenuPopperOpened(false);
  }, []);

  const { whiteboardSettings } = useRoomActionsStore(
    useShallow((state) => ({
      whiteboardSettings: state.whiteboardSettings,
    }))
  );

  const {
    canvasRef,
    isMirrorActive,
    isFullscreenActive,

    handlePinVideo,
    handleSnapshot,
    handleFullScreen,
    handleVideoMirror,
    handlePictureInPicture,
  } = useVideoContextMenu(videoStream, videoRef);

  const streamUid = useMemo(() => Number(videoStream.id), [videoStream.id]);

  const stream = useMemo(() => {
    // If it's another user or screen share, just use their stream
    if (!isCurrentUser || type !== 'videoType') {
      return videoMediaStream;
    }
    const { appliedEffect } = useVirtualBackgroundStore.getState();
    // Standard behavior: Modified -> Original -> Raw
    // Anti-Flicker: If VB is active but we don't have the modified stream yet,
    // return a stable empty stream (black video) to prevent showing the raw background.
    // This prevents the user from seeing their raw background for a split second.
    if (isCurrentUser && appliedEffect?.type !== 'none' && !modifiedStream) {
      // Reuse the same empty stream instead of creating new ones
      // Only create once, then reuse across all re-renders
      if (!emptyStreamRef.current) {
        emptyStreamRef.current = new MediaStream();
      }
      return emptyStreamRef.current;
    }

    return modifiedStream || originalStream;
  }, [isCurrentUser, modifiedStream, originalStream, type, videoMediaStream]);

  const currentPeer = useMemo(() => peers.get(peerId), [peerId, peers]);
  const isMicMuted = useMemo(
    () => (currentPeer?.peer_info ? !currentPeer.peer_info.peer_audio : audioVolume === 0 && isVideoAudioMuted),
    [audioVolume, currentPeer?.peer_info, isVideoAudioMuted]
  );

  const audioVolumeTmp = useMemo(() => audioVolume * 10, [audioVolume]);
  const peerEmoji: TEmojiId | undefined = useMemo(
    () => currentPeer?.peer_emoji || undefined,
    [currentPeer?.peer_emoji]
  );
  const handRaiseOrder = useMemo(() => {
    const index = raisedHandsQueue.indexOf(videoStream.peerId);
    return index !== -1 ? index + 1 : null;
  }, [raisedHandsQueue, videoStream.peerId]);

  const wavyScale = useMemo(() => 1 + audioVolumeTmp / 10, [audioVolumeTmp]);

  const isPinnedForViewers = useMemo(
    () => !!streamUid && viewersPinnedPeerUids.includes(streamUid),
    [streamUid, viewersPinnedPeerUids]
  );

  // Show MagicPen only for screen shares when:
  // 1. It's a screen share (screenType) (as long as it's not manually unpinned)
  // 2. Current user is sharing OR host is viewing
  // 3. No other pinned video box exists (from Pin action)
  // 4. Whiteboard is NOT active
  const shouldShowMagicPen = useMemo(() => {
    const isScreenShare = type === 'screenType';

    return isScreenShare && !isPipBox;
  }, [type, isPipBox]);

  const canAnnotate = useMemo(
    () => isCurrentPeerSharing || isHost || !!isFloatingButtonEnabled,
    [isCurrentPeerSharing, isHost, isFloatingButtonEnabled]
  );

  const { borderColor, backgroundColor } = useMemo(
    () => ({
      borderColor: peerColor ? createElementColor(peerColor, 'video-border') : null,
      backgroundColor: peerColor ? createElementColor(peerColor, 'video-background') : null,
    }),
    [peerColor]
  );

  const handlePinForViewers = useCallback(() => {
    if (!streamUid) {
      return;
    }

    const current = viewersPinnedPeerUids || [];
    const exists = current.includes(streamUid);

    if (exists) {
      // Unpin - emit UNPIN event
      SocketService.emit(EWebinarSockets.VIEWER_IFRAME_LAYOUT, {
        layout: 'UNPIN',
        unpinUserIds: [streamUid],
      });
    } else {
      // Pin - emit PIN event with updated list
      const next = [...current, streamUid];
      SocketService.emit(EWebinarSockets.VIEWER_IFRAME_LAYOUT, {
        layout: 'PIN',
        pinUserIds: next,
      });
    }

    // Update local state
    toggleViewerPinForPeerUid(streamUid);
  }, [streamUid, viewersPinnedPeerUids, toggleViewerPinForPeerUid]);

  const handleResetStreamVolume = useCallback(() => {
    useVideoStreamStore.getState().updateVideo(peerId, { audioVolume: 0 });
  }, [peerId]);

  const debouncedResetStreamVolume = useMemo(() => debounce(handleResetStreamVolume, 700), [handleResetStreamVolume]);
  const debouncedSetPeerNotTalking = useMemo(() => debounce(() => setIsPeerTalking(false), 2000), []);

  useEffect(() => {
    if (!isMicMuted && audioVolume > 3) {
      setIsPeerTalking(true);
      debouncedSetPeerNotTalking.cancel?.();
    } else if (isMicMuted) {
      setIsPeerTalking(false);
      debouncedSetPeerNotTalking.cancel?.();
    } else {
      debouncedSetPeerNotTalking();
    }

    return () => {
      debouncedSetPeerNotTalking.cancel?.();
    };
  }, [audioVolume, isMicMuted, debouncedSetPeerNotTalking]);

  useEffect(() => {
    debouncedResetStreamVolume();
  }, [audioVolumeTmp, debouncedResetStreamVolume]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = getMediaStreamSource(stream, 'videoType');
    } else if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Cleanup empty stream on unmount
  useEffect(() => {
    return () => {
      if (emptyStreamRef.current) {
        // Stop all tracks (defensive, empty stream has no tracks but good practice)
        emptyStreamRef.current.getTracks().forEach((track) => track.stop());
        emptyStreamRef.current = null;
      }
    };
  }, []);

  const videoActions = useMemo(() => {
    if (stream)
      return [
        ...(isHostOrCohost && meetingType === EMeetingType.WEBINAR
          ? [
              {
                label: isPinnedForViewers ? 'Unpin for viewers' : 'Pin for viewers',
                action: handlePinForViewers,
              },
            ]
          : []),
        { label: isPinned ? 'Unpin' : 'Pin', action: handlePinVideo },
        { label: 'Full Screen', action: handleFullScreen },
        { label: 'Mirror', action: handleVideoMirror },
        { label: 'Snapshot', action: handleSnapshot },
        { label: 'Picture In Picture', action: handlePictureInPicture, enable: document.pictureInPictureEnabled },
      ].filter((action) => action.enable !== false);

    return [{ label: isPinned ? 'Unpin' : 'Pin', action: handlePinVideo }];
  }, [
    handleFullScreen,
    handlePictureInPicture,
    handlePinForViewers,
    handlePinVideo,
    handleSnapshot,
    handleVideoMirror,
    isHostOrCohost,
    isPinned,
    isPinnedForViewers,
    meetingType,
    stream,
  ]);

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  // Handle visibility changes
  useEffect(() => {
    if (!stream || !videoStream.id) return;

    if (!isPipBox) {
      handleVisibilityChange(isTabActive);
    } else {
      handleVisibilityChange(true);
    }
  }, [isTabActive, videoStream.id, isPipBox, stream, handleVisibilityChange]);

  // Update cleanup function when dependencies change
  useEffect(() => {
    cleanupRef.current = () => {
      if (videoStream.id) {
        handleVisibilityChange(false);
      }
    };
  }, [videoStream.id, handleVisibilityChange]);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  useEffect(() => {
    if ((objectFit || selectedVideoObjectFit) === 'scale-down') {
      document.documentElement.style.setProperty('--videoObjFitWidthHeight', '90%');
    } else {
      document.documentElement.style.setProperty('--videoObjFitWidthHeight', '100%');
    }
  }, [objectFit, selectedVideoObjectFit]);

  return {
    wavyScale,
    videoRef,
    peerEmoji,
    isMicMuted,
    isPeerTalking,
    audioVolumeTmp,
    raisedHandsQueue,
    selectedVideoObjectFit,
    openDrawer,

    // raise hand logic
    handRaiseOrder,
    raiseHand: () => raiseHand(videoStream.peerId),
    lowerHand: () => lowerHand(videoStream.peerId),

    isMenuPopperOpened,
    hoveredVideoBox,

    // menu context
    canvasRef,
    isMirrorActive,
    isFullscreenActive,

    whiteboardSettings,

    pip,
    peerColor,
    borderColor,
    backgroundColor,
    animations,
    containerRef,
    isDrawerOpen,
    isMobileDevice,
    isHandRaised: handRaiseOrder !== null,
    shouldShowMagicPen,
    canAnnotate,

    videoDimensions,

    setIsMenuPopperOpened,
    handlePinVideo,
    handleSnapshot,
    handleFullScreen,
    handleVideoMirror,
    handlePictureInPicture,
    handleMouseEnter,
    handleMouseLeave,
    handleDrawerToggle,
    videoActions,
    stream,
  };
};

export default useVideoBox;
